import { useRef, useEffect, useState } from 'react';

import { usePageHighlight } from '@/providers/PageHighlightContext';
import { useRefContext } from '@/providers/RefContext';
import { Container, RhLink } from '@components';

import Image from 'next/image';

import NewsletterForm from './NewsletterForm';
import SocialLinks from './SocialLinks';
import FooterColumns from './FooterColumns';
import FooterLogo from '@/assets/svg/footer-logo.svg';
import FooterLogoStacked from '@/assets/svg/footer-logo-stacked.svg';
import CuyahogaLogo from '@/assets/images/cuyahoga-arts-logo.png';
import OhioLogo from '@/assets/images/ohio-arts-logo.png';
import UnitedLogo from '@/assets/images/logo-united-white.png';

import classNames from 'classnames/bind';
import styles from './Footer.module.scss';
const cx = classNames.bind(styles);

export default function Footer(props: FooterSettings) {
  const { highlightColor } = usePageHighlight();
  const { setFooterRef } = useRefContext();
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    setStyle({ "--highlight-color": highlightColor } as React.CSSProperties);
  }, [highlightColor]);

  if (!props?.footerSettingsFields) return;

  const { footerSettingsFields } = props;

  const {
    newsletterForm,
    socialLinks,
    findUs,
    footerColumns,
    legalCopy
  } = footerSettingsFields;

  // -------------------- Render

  return (
    <div className={cx("footer-wrapper")}>
      <footer
        id="footer"
        className={cx("footer")}
        ref={footerRef => setFooterRef(footerRef)}
        style={style}
      >

        {/* Newsletter Form */}
        <Container>
          <NewsletterForm {...newsletterForm} />
        </Container>

        <hr className={cx("divider")}/>

        {/* Social Links, Find Us, Columns, Legal */}
        <Container className={cx("footer-container")}>

          {/* Social Links */}
          <SocialLinks socialLinks={socialLinks} />

          {/* Find Us + Columns */}
          <div className={cx("find-us-columns-wrapper")}>

            {/* Find Us */}
            <div className={cx(["find-us"])}>
              <p className={cx("eyebrow-s")} role="heading" aria-level={2}>{findUs.label}</p>
              <div className={cx("find-us__copy")}>
                <a className={cx(["text-m"])} href={findUs.address.mapLink} target="_blank" data-gtm-section="footer">
                  <span dangerouslySetInnerHTML={{ __html: findUs.address.text }} />
                </a>
                <a className={cx("text-m")} href={`tel:${findUs.phone.number}`} target="_blank" data-gtm-section="footer">
                  {findUs.phone.label}
                </a>
                <a className={cx("text-m")} href={`mailto:${findUs.email.email}`} target="_blank" data-gtm-section="footer">
                  {findUs.email.label}
                </a>
              </div>
            </div>

            {/* Columns */}
            <FooterColumns footerColumns={footerColumns} />

          </div>

          {/* Legal and Logos */}
          <div className={cx("footer-legal")}>
            <p className={cx(["text-m"])}>{legalCopy}</p>
            <div className={cx("footer-logos")}>
              <RhLink className={cx("logo", "logo--ohio")} href="https://oac.ohio.gov/" target="_blank" data-gtm-section="footer">
                <Image src={OhioLogo} alt="Ohio Arts Council" unoptimized />
              </RhLink>
              <RhLink className={cx("logo", "logo--cuyahoga")} href="https://www.cacgrants.org/" target="_blank" data-gtm-section="footer">
                <Image src={CuyahogaLogo} alt="Cuyahoga Arts & Culture" unoptimized />
              </RhLink>
              <RhLink className={cx("logo", "logo--united")} href="https://www.united.com/" target="_blank" data-gtm-section="footer">
                <Image src={UnitedLogo} alt="United Airlines" unoptimized />
              </RhLink>
            </div>
          </div>

        </Container>

        {/* Logo (big at bottom) */}
        <div className={cx("footer-logo-wrapper")}>
          <FooterLogoStacked className={cx("footer-logo-stacked")} />
          <FooterLogo className={cx("footer-logo")} />
        </div>

      </footer>
    </div>
  );

}
